/**
 * Elevation tokens — design --shadow-sm … --shadow-lg.
 * Web uses boxShadow (react-native-web honors it); native uses the RN
 * shadow* / elevation props. Use `shadow(name, isDark)` to get the right
 * style object for the current platform + theme.
 */
import { Platform, ViewStyle } from 'react-native';

type ShadowName = 'sm' | 'base' | 'md' | 'lg' | 'focus';

const WEB_LIGHT: Record<ShadowName, string> = {
  sm: '0 1px 0 rgba(16,24,40,.04)',
  base: '0 1px 2px rgba(16,24,40,.06), 0 4px 12px -4px rgba(16,24,40,.08)',
  md: '0 2px 6px rgba(16,24,40,.08), 0 10px 24px -6px rgba(16,24,40,.12)',
  lg: '0 8px 24px rgba(16,24,40,.10), 0 24px 48px -12px rgba(16,24,40,.18)',
  focus: '0 0 0 3px rgba(43,100,195,.18)',
};

const WEB_DARK: Record<ShadowName, string> = {
  sm: '0 1px 0 rgba(0,0,0,.30)',
  base: '0 1px 2px rgba(0,0,0,.40), 0 4px 12px -4px rgba(0,0,0,.55)',
  md: '0 2px 6px rgba(0,0,0,.50), 0 8px 20px -6px rgba(0,0,0,.65)',
  lg: '0 4px 14px rgba(0,0,0,.50), 0 16px 32px -12px rgba(0,0,0,.65)',
  focus: '0 0 0 3px rgba(98,162,255,.30)',
};

const NATIVE: Record<ShadowName, ViewStyle> = {
  sm: { shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 1, elevation: 1 },
  base: { shadowColor: '#101828', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 3 },
  md: { shadowColor: '#101828', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.14, shadowRadius: 16, elevation: 6 },
  lg: { shadowColor: '#101828', shadowOffset: { width: 0, height: 16 }, shadowOpacity: 0.2, shadowRadius: 32, elevation: 12 },
  focus: { shadowColor: '#2b64c3', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.3, shadowRadius: 3, elevation: 0 },
};

export function shadow(name: ShadowName, isDark = false): ViewStyle {
  if (Platform.OS === 'web') {
    return { boxShadow: (isDark ? WEB_DARK : WEB_LIGHT)[name] } as unknown as ViewStyle;
  }
  return NATIVE[name];
}
